import { useState } from "react";

// Painel de pagamento do evento. Só deve ser mostrado para inscrições aprovadas
// (a liberação passa pelo link assinado enviado depois da aprovação).
interface PaymentPanelProps {
  eventTitle: string;
  name: string;
  amountUsd?: number;
}

const ZELLE_PHONE = "(305) 733-2401";
const ZELLE_NAME = "IPG Enterprise, LLC";
const ZELLE_QR = "/img/zelle-ipg-enterprise-qr.jpg";
const SUPPORT_WHATSAPP = "+1 (205) 892-5956";

export default function PaymentPanel({ eventTitle, name, amountUsd = 15 }: PaymentPanelProps) {
  const [copied, setCopied] = useState(false);

  async function copyPhone() {
    try {
      await navigator.clipboard.writeText(ZELLE_PHONE.replace(/\D/g, ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Navegadores que recusam o clipboard: o número continua visível para selecionar.
    }
  }

  return (
    <section className="py-16 md:py-24 bg-beige">
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl p-6 md:p-10">
          <div className="text-center mb-8">
            <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
              Vaga pré-reservada
            </p>
            <h2 className="font-display text-xl md:text-2xl text-brown mb-2">
              Falta só o pagamento
            </h2>
            <p className="font-body text-sm text-brown/60">
              {name}, sua vaga em <strong>{eventTitle}</strong> está pré-reservada por <strong>48 horas</strong>.
              Ela só fica confirmada depois do pagamento.
            </p>
          </div>

          <div className="rounded-xl bg-beige/60 p-5 mb-6 space-y-3">
            <div className="flex justify-between gap-4 font-body text-sm">
              <span className="text-brown/60">Valor</span>
              <span className="text-brown font-semibold">US$ {amountUsd}</span>
            </div>
            <div className="flex justify-between gap-4 font-body text-sm">
              <span className="text-brown/60">Pagar por</span>
              <span className="text-brown font-semibold">Zelle</span>
            </div>
            <div className="flex justify-between gap-4 font-body text-sm">
              <span className="text-brown/60">Número Zelle</span>
              <span className="text-brown font-semibold">{ZELLE_PHONE}</span>
            </div>
            <div className="flex justify-between gap-4 font-body text-sm">
              <span className="text-brown/60">Nome</span>
              <span className="text-brown font-semibold">{ZELLE_NAME}</span>
            </div>
            <div className="flex justify-between gap-4 font-body text-sm">
              <span className="text-brown/60">Mensagem do pagamento</span>
              <span className="text-brown font-semibold">{`CRIADAS ${name.split(" ")[0]}`}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={copyPhone}
            className="w-full mb-6 px-6 py-3.5 rounded-full border border-primary text-primary font-body text-sm tracking-wide uppercase transition-all duration-300 hover:bg-primary/5 whitespace-nowrap cursor-pointer"
          >
            {copied ? "Número copiado" : "Copiar número Zelle"}
          </button>

          <div className="text-center mb-6">
            <p className="font-body text-xs text-brown/60 mb-4">
              Ou escaneie o QR code no app do seu banco:
            </p>
            <img
              src={ZELLE_QR}
              alt={`QR code Zelle para ${ZELLE_NAME}`}
              className="mx-auto w-full max-w-xs rounded-xl"
            />
          </div>

          <ol className="font-body text-sm text-brown/70 leading-relaxed list-decimal pl-5 space-y-2">
            <li>Abra o app do seu banco e escolha Zelle.</li>
            <li>Envie US$ {amountUsd} para {ZELLE_PHONE} (ou escaneie o QR code).</li>
            <li>Na mensagem, escreva CRIADAS e seu primeiro nome.</li>
            <li>Mande o comprovante no WhatsApp {SUPPORT_WHATSAPP}.</li>
          </ol>
        </div>
      </div>
    </section>
  );
}
